import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save, Plus, Trash2, GripVertical, ArrowUp, ArrowDown, Settings2, Users, Shield, Home, Activity, FileText } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import UserManagement from '@/pages/UserManagement';
import RoleEditor from '@/pages/RoleEditor';
import AuditLog from '@/pages/AuditLog';
import ProposalContentEditor from '@/pages/ProposalContentEditor';
import { useAuth } from '@/contexts/AuthContext';

export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('company');

  useEffect(() => {
    api.get('/settings').then(r => { setSettings(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    await api.put('/settings', settings);
    toast.success('Settings saved');
  };

  const updateSchedule = (idx, field, value) => {
    const s = [...settings.default_payment_schedule];
    s[idx] = { ...s[idx], [field]: field === 'pct' ? parseFloat(value) || 0 : value };
    setSettings(prev => ({ ...prev, default_payment_schedule: s }));
  };

  const addScheduleItem = () => {
    setSettings(prev => ({
      ...prev,
      default_payment_schedule: [...prev.default_payment_schedule, { name: 'New Milestone', pct: 0, amount: 0 }]
    }));
  };

  const removeScheduleItem = (idx) => {
    setSettings(prev => ({
      ...prev,
      default_payment_schedule: prev.default_payment_schedule.filter((_, i) => i !== idx)
    }));
  };

  if (loading) return <div className="p-8"><div className="h-40 bg-white rounded-xl animate-pulse" /></div>;
  if (!settings) return null;

  const totalPct = settings.default_payment_schedule?.reduce((s, i) => s + (i.pct || 0), 0) || 0;

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="settings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Settings</h1>
          <p className="text-slate-500 text-sm mt-1">Manage company defaults, team, and permissions</p>
        </div>
        {activeTab === 'company' && (
          <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={handleSave} data-testid="save-settings-btn">
            <Save className="w-4 h-4 mr-1" /> Save Changes
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="company" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5" data-testid="tab-company">
            <Settings2 className="w-3.5 h-3.5" /> Company
          </TabsTrigger>
          {user?.role === 'admin' && (
            <TabsTrigger value="users" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5" data-testid="tab-users">
              <Users className="w-3.5 h-3.5" /> Team
            </TabsTrigger>
          )}
          {user?.role === 'admin' && (
            <TabsTrigger value="roles" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5" data-testid="tab-roles">
              <Shield className="w-3.5 h-3.5" /> Roles & Access
            </TabsTrigger>
          )}
          {user?.role === 'admin' && (
            <TabsTrigger value="data" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5" data-testid="tab-data">
              <Save className="w-3.5 h-3.5" /> Data & Exports
            </TabsTrigger>
          )}
          {user?.role === 'admin' && (
            <TabsTrigger value="homeowners" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5" data-testid="tab-homeowners">
              <Home className="w-3.5 h-3.5" /> Homeowner Accounts
            </TabsTrigger>
          )}
          {user?.role === 'admin' && (
            <TabsTrigger value="audit" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5" data-testid="tab-audit">
              <Activity className="w-3.5 h-3.5" /> Audit Log
            </TabsTrigger>
          )}
          {user?.role === 'admin' && (
            <TabsTrigger value="proposal-content" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5" data-testid="tab-proposal-content">
              <FileText className="w-3.5 h-3.5" /> Proposal Content
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="company">
      <div className="space-y-6">
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-base">Margin Targets</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <Label>Minimum Gross Margin %</Label>
                <Input type="number" step="1" value={settings.min_margin_pct || 20} data-testid="settings-min-margin"
                  onChange={e => setSettings(p => ({ ...p, min_margin_pct: parseFloat(e.target.value) || 0 }))} />
                <p className="text-[10px] text-red-500 mt-1">Bids below this will show a warning</p>
              </div>
              <div>
                <Label>Target Gross Margin %</Label>
                <Input type="number" step="1" value={settings.target_margin_pct || 25} data-testid="settings-target-margin"
                  onChange={e => setSettings(p => ({ ...p, target_margin_pct: parseFloat(e.target.value) || 0 }))} />
                <p className="text-[10px] text-slate-400 mt-1">Aim for this on base builds</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Additional Overhead %</Label>
                <Input type="number" step="0.5" value={settings.overhead_pct} data-testid="settings-overhead"
                  onChange={e => setSettings(p => ({ ...p, overhead_pct: parseFloat(e.target.value) || 0 }))} />
                <p className="text-[10px] text-slate-400 mt-1">Added on top of sell prices (0 if margin is in items)</p>
              </div>
              <div>
                <Label>Additional Profit %</Label>
                <Input type="number" step="0.5" value={settings.profit_pct} data-testid="settings-profit"
                  onChange={e => setSettings(p => ({ ...p, profit_pct: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <Label>Contingency %</Label>
                <Input type="number" step="0.5" value={settings.contingency_pct} data-testid="settings-contingency"
                  onChange={e => setSettings(p => ({ ...p, contingency_pct: parseFloat(e.target.value) || 0 }))} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Rock Dig Contingency Range</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <div>
              <Label>Min %</Label>
              <Input type="number" step="1" value={settings.rock_dig_contingency_min} data-testid="settings-rock-min"
                onChange={e => setSettings(p => ({ ...p, rock_dig_contingency_min: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div>
              <Label>Max %</Label>
              <Input type="number" step="1" value={settings.rock_dig_contingency_max} data-testid="settings-rock-max"
                onChange={e => setSettings(p => ({ ...p, rock_dig_contingency_max: parseFloat(e.target.value) || 0 }))} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Default Payment Schedule</CardTitle>
              <Button size="sm" variant="outline" onClick={addScheduleItem} data-testid="add-schedule-item-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {settings.default_payment_schedule?.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <Input value={item.name} onChange={e => updateSchedule(idx, 'name', e.target.value)}
                  className="flex-1" data-testid={`schedule-name-${idx}`} />
                <div className="flex items-center gap-1 w-24">
                  <Input type="number" step="1" value={item.pct} onChange={e => updateSchedule(idx, 'pct', e.target.value)}
                    className="w-16" data-testid={`schedule-pct-${idx}`} />
                  <span className="text-sm text-slate-500">%</span>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => removeScheduleItem(idx)}
                  data-testid={`schedule-remove-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
            <div className={`text-sm font-mono mt-2 ${Math.abs(totalPct - 100) < 0.01 ? 'text-emerald-600' : 'text-red-500'}`}>
              Total: {totalPct}% {Math.abs(totalPct - 100) >= 0.01 && '(must equal 100%)'}
            </div>
          </CardContent>
        </Card>

        {/* Construction Stage Templates */}
        <Card className="border-slate-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Construction Stages</CardTitle>
              <Button size="sm" variant="outline" onClick={() => {
                const st = [...(settings.stage_templates || [])];
                st.push({ name: 'New Stage', order: st.length });
                setSettings(p => ({ ...p, stage_templates: st }));
              }} data-testid="add-stage-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Stage
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-1">Drag to reorder. These define the pipeline for every new project.</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {(settings.stage_templates || []).sort((a, b) => a.order - b.order).map((stage, idx) => (
              <div key={idx} className="flex items-center gap-2 group">
                <div className="flex flex-col gap-0.5">
                  <Button variant="ghost" size="icon" className="h-4 w-4 text-slate-300 hover:text-slate-600"
                    onClick={() => {
                      if (idx === 0) return;
                      const st = [...(settings.stage_templates || [])];
                      [st[idx], st[idx - 1]] = [st[idx - 1], st[idx]];
                      st.forEach((s, i) => { s.order = i; });
                      setSettings(p => ({ ...p, stage_templates: st }));
                    }} data-testid={`stage-up-${idx}`}>
                    <ArrowUp className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-4 w-4 text-slate-300 hover:text-slate-600"
                    onClick={() => {
                      const st = [...(settings.stage_templates || [])];
                      if (idx >= st.length - 1) return;
                      [st[idx], st[idx + 1]] = [st[idx + 1], st[idx]];
                      st.forEach((s, i) => { s.order = i; });
                      setSettings(p => ({ ...p, stage_templates: st }));
                    }} data-testid={`stage-down-${idx}`}>
                    <ArrowDown className="w-3 h-3" />
                  </Button>
                </div>
                <span className="text-xs text-slate-400 font-mono w-6 text-center">{idx + 1}</span>
                <Input
                  value={stage.name}
                  onChange={e => {
                    const st = [...(settings.stage_templates || [])];
                    st[idx] = { ...st[idx], name: e.target.value };
                    setSettings(p => ({ ...p, stage_templates: st }));
                  }}
                  className="flex-1 h-9"
                  data-testid={`stage-name-${idx}`}
                />
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => {
                    const st = (settings.stage_templates || []).filter((_, i) => i !== idx);
                    st.forEach((s, i) => { s.order = i; });
                    setSettings(p => ({ ...p, stage_templates: st }));
                  }} data-testid={`stage-remove-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        {user?.role === 'admin' && (
          <TabsContent value="users">
            <UserManagement />
          </TabsContent>
        )}
        {user?.role === 'admin' && (
          <TabsContent value="roles">
            <RoleEditor />
          </TabsContent>
        )}
        {user?.role === 'admin' && (
          <TabsContent value="data">
            <DataManagement />
          </TabsContent>
        )}
        {user?.role === 'admin' && (
          <TabsContent value="homeowners">
            <HomeownerManager />
          </TabsContent>
        )}
        {user?.role === 'admin' && (
          <TabsContent value="audit">
            <AuditLog />
          </TabsContent>
        )}
        {user?.role === 'admin' && (
          <TabsContent value="proposal-content">
            <ProposalContentEditor />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function DataManagement() {
  const [stats, setStats] = useState(null);
  const [downloading, setDownloading] = useState('');

  useEffect(() => {
    api.get('/export/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const handleExport = async (type, filename) => {
    setDownloading(type);
    try {
      const r = await api.get(`/export/${type}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      toast.success(`${type} exported`);
    } catch { toast.error('Export failed'); }
    setDownloading('');
  };

  const exports = [
    { key: 'bids', label: 'Bids', file: 'hdh_bids_export.csv', desc: 'All bids with customer, totals, margins' },
    { key: 'leads', label: 'Leads', file: 'hdh_leads_export.csv', desc: 'All leads with contact info, stage' },
    { key: 'projects', label: 'Projects', file: 'hdh_projects_export.csv', desc: 'All projects with status, progress' },
    { key: 'expenses', label: 'Expenses', file: 'hdh_expenses_export.csv', desc: 'All expenses by project' },
    { key: 'vendors', label: 'Vendors', file: 'hdh_vendors_export.csv', desc: 'Vendor directory with insurance' },
    { key: 'full-backup', label: 'Full Backup (JSON)', file: 'hdh_full_backup.json', desc: 'Complete database backup — all data' },
  ];

  return (
    <div className="space-y-6" data-testid="data-management">
      <div>
        <h2 className="text-lg font-bold text-[#1e3a50]">Data & Exports</h2>
        <p className="text-sm font-medium text-[#6b8a9e]">Download your business data and create backups</p>
      </div>

      {stats && (
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-bold">Database Overview</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {Object.entries(stats.counts || {}).filter(([_, v]) => v > 0).map(([name, count]) => (
                <div key={name} className="p-3 bg-[#f5f9fb] rounded-lg text-center">
                  <p className="text-lg font-bold font-mono text-[#2a6496]">{count.toLocaleString()}</p>
                  <p className="text-[10px] font-bold text-[#6b8a9e] capitalize">{name.replace(/_/g, ' ')}</p>
                </div>
              ))}
            </div>
            <p className="text-xs font-semibold text-[#4a9ac5] mt-3">{stats.total_records?.toLocaleString()} total records</p>
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-bold">Export Data</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {exports.map(exp => (
            <div key={exp.key} className="flex items-center justify-between p-3 bg-[#f5f9fb] rounded-lg hover:bg-[#eef5f9] transition-colors">
              <div>
                <p className="text-sm font-bold text-[#1e3a50]">{exp.label}</p>
                <p className="text-[10px] font-medium text-[#6b8a9e]">{exp.desc}</p>
              </div>
              <Button size="sm" variant="outline" className="border-[#c8dce8] text-[#4a9ac5] font-bold"
                onClick={() => handleExport(exp.key, exp.file)} disabled={downloading === exp.key}
                data-testid={`export-${exp.key}`}>
                {downloading === exp.key ? 'Downloading...' : 'Download'}
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] border-l-4 border-l-amber-400">
        <CardContent className="p-4">
          <p className="text-sm font-bold text-amber-700 mb-1">Production Deployment Notes</p>
          <ul className="text-xs font-medium text-amber-600 space-y-1">
            <li>For production, connect to <strong>MongoDB Atlas</strong> (cloud) by updating MONGO_URL in .env</li>
            <li>Move photo storage to <strong>AWS S3 or Cloudflare R2</strong> for scalability (photos currently in MongoDB)</li>
            <li>Set up <strong>automated daily backups</strong> via MongoDB Atlas scheduled snapshots</li>
            <li>Database indexes are configured and will be created automatically on startup</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}


function HomeownerManager() {
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', projects: [] });
  const [createdCreds, setCreatedCreds] = useState(null);

  useEffect(() => {
    api.get('/homeowner/admin/accounts').then(r => setAccounts(r.data)).catch(() => {});
    api.get('/projects').then(r => { const d = Array.isArray(r.data) ? r.data : r.data.data || []; setProjects(d); }).catch(() => {});
  }, []);

  const handleCreate = async () => {
    try {
      const r = await api.post('/homeowner/admin/create-account', form);
      setCreatedCreds({ email: r.data.email, password: r.data.temp_password, name: r.data.name });
      toast.success('Homeowner account created');
      api.get('/homeowner/admin/accounts').then(r => setAccounts(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="homeowner-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#1e3a50]">Homeowner Accounts</h2>
          <p className="text-sm font-medium text-[#6b8a9e]">{accounts.length} accounts | Portal: /portal/login</p>
        </div>
        <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={() => { setForm({ name: '', email: '', phone: '', password: '', projects: [] }); setCreatedCreds(null); setDialogOpen(true); }}
          data-testid="create-homeowner-btn">
          <Plus className="w-4 h-4 mr-1" /> Create Homeowner Login
        </Button>
      </div>

      {/* Credentials banner */}
      {createdCreds && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <p className="text-sm font-bold text-emerald-800 mb-2">Account Created — Share These Credentials</p>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div><p className="font-bold text-emerald-700">Name</p><p className="font-mono text-emerald-900">{createdCreds.name}</p></div>
              <div><p className="font-bold text-emerald-700">Email</p><p className="font-mono text-emerald-900">{createdCreds.email}</p></div>
              <div><p className="font-bold text-emerald-700">Temp Password</p><p className="font-mono text-emerald-900 select-all">{createdCreds.password}</p></div>
            </div>
            <p className="text-[10px] font-medium text-emerald-600 mt-2">Portal URL: {window.location.origin}/portal/login</p>
            <Button size="sm" variant="outline" className="mt-2 text-xs border-emerald-300 text-emerald-700" onClick={() => {
              navigator.clipboard.writeText(`Your HDH Pools & Spas portal login:\nURL: ${window.location.origin}/portal/login\nEmail: ${createdCreds.email}\nPassword: ${createdCreds.password}`);
              toast.success('Copied to clipboard');
            }}>Copy Login Info</Button>
          </CardContent>
        </Card>
      )}

      {/* Accounts list */}
      <div className="space-y-2">
        {accounts.map(ho => (
          <Card key={ho.id} className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid={`ho-${ho.id}`}>
            <CardContent className="p-3 flex items-center gap-4">
              <div className="w-9 h-9 rounded-full bg-[#4a9ac5]/15 flex items-center justify-center flex-shrink-0">
                <Home className="w-4 h-4 text-[#2a6496]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#1e3a50]">{ho.name}</p>
                <p className="text-[11px] font-medium text-[#6b8a9e]">{ho.email} {ho.phone ? `| ${ho.phone}` : ''}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold text-[#4a9ac5]">{(ho.projects || []).length} project(s)</p>
                <p className="text-[9px] text-[#8faab8]">Created {new Date(ho.created_at).toLocaleDateString()}</p>
              </div>
            </CardContent>
          </Card>
        ))}
        {accounts.length === 0 && <p className="text-center py-8 text-sm font-medium text-[#6b8a9e]">No homeowner accounts yet</p>}
      </div>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="create-homeowner-dialog">
          <DialogHeader><DialogTitle>Create Homeowner Login</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold">Homeowner Name *</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="John & Sarah Miller" data-testid="ho-form-name" /></div>
            <div><Label className="text-xs font-bold">Email *</Label><Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="customer@email.com" data-testid="ho-form-email" /></div>
            <div><Label className="text-xs font-bold">Phone</Label><Input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} placeholder="435-555-0123" /></div>
            <div><Label className="text-xs font-bold">Temporary Password</Label><Input value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} placeholder="Leave blank to auto-generate" /><p className="text-[9px] text-[#6b8a9e] mt-0.5">If blank, a secure password will be generated</p></div>
            <div>
              <Label className="text-xs font-bold">Link to Project(s)</Label>
              <div className="space-y-1 mt-1 max-h-40 overflow-y-auto">
                {projects.map(p => (
                  <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#f5f9fb] cursor-pointer">
                    <input type="checkbox" checked={form.projects.includes(p.id)}
                      onChange={e => setForm(prev => ({...prev, projects: e.target.checked ? [...prev.projects, p.id] : prev.projects.filter(x => x !== p.id)}))} />
                    <span className="text-xs font-bold text-[#1e3a50]">{p.name}</span>
                    <span className="text-[10px] text-[#6b8a9e]">{p.customer_city}</span>
                  </label>
                ))}
                {projects.length === 0 && <p className="text-[10px] text-[#6b8a9e]">No projects available. Create a project first.</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={handleCreate} disabled={!form.name || !form.email} data-testid="ho-form-save">Create Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
